import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Mail } from "lucide-react";
import SEO from "../components/SEO";

const SECTIONS = [
  {
    id: "research-use",
    label: "Research Use Only & Positioning",
    items: [
      {
        q: "What does “Research Use Only” (RUO) mean?",
        a: "Research Use Only (RUO) compounds are intended exclusively for laboratory and scientific research purposes. These products are supplied for in vitro research and investigational use only. They are not intended for human consumption, medical use, or any therapeutic applications.",
      },
      {
        q: "Who can purchase from LuviaRx?",
        a: "LuviaRx products are available to customers 21 years and older within the United States. By making a purchase, you agree to our Terms of Service, which clearly state that all products are for research use only and not for human consumption.",
      },
      {
        q: "Do I need special credentials to order?",
        a: "No special credentials or verification process is required. However, by purchasing from LuviaRx, you certify that you understand and agree that all products are for research use only. You assume full responsibility for compliance with all applicable laws and regulations in your jurisdiction.",
      },
      {
        q: "What is my responsibility as a purchaser?",
        a: "As a purchaser, you are responsible for understanding and complying with all local, state, and federal laws regarding the purchase and use of research compounds. LuviaRx products must be used only for legitimate research purposes in appropriate laboratory settings.",
      },
    ],
  },
  {
    id: "shipping",
    label: "Shipping & Delivery",
    items: [
      {
        q: "Where do you ship?",
        a: "LuviaRx currently ships to the United States only. We do not ship to APO/FPO addresses or international locations at this time.",
      },
      {
        q: "How long does delivery take?",
        a: "Orders typically ship within 1–2 business days and arrive within 3–5 business days via standard carrier services. You will receive tracking information via email once your order ships.",
      },
      {
        q: "What's included in my package?",
        a: 'Each shipment includes your lyophilized, sealed research compounds in protective packaging. All packages are clearly labeled "FOR RESEARCH USE ONLY" as required by law. You will also receive a Certificate of Analysis (COA) detailing purity, batch information, and third-party testing results.',
      },
      {
        q: "Are packages discreet?",
        a: "All packages are shipped in standard protective packaging with appropriate labeling for research compounds.",
      },
    ],
  },
  {
    id: "quality",
    label: "Product Quality & Testing",
    items: [
      {
        q: "How pure are your compounds?",
        a: "All LuviaRx compounds meet a minimum purity threshold of 98% and are tested by third-party laboratories. Specific purity percentages and testing results are included with each order's Certificate of Analysis.",
      },
      {
        q: "What is a Certificate of Analysis (COA)?",
        a: "A COA is a detailed report from third-party testing laboratories confirming the purity, identity, and quality of each batch. It includes batch numbers for full traceability and is included with every LuviaRx order.",
      },
      {
        q: "Are your compounds lab-grade?",
        a: "Yes. All LuviaRx compounds are handled to lab-grade standards, including proper lyophilization, sealing, and storage protocols. We maintain rigorous quality control throughout our supply chain.",
      },
      {
        q: "What testing is performed?",
        a: "Each batch undergoes third-party testing to verify purity, identity, and absence of contaminants. Testing methods meet industry research standards.",
      },
    ],
  },
  {
    id: "storage",
    label: "Storage & Handling",
    items: [
      {
        q: "How should I store my compounds?",
        a: "Storage instructions vary by compound. General guidelines: lyophilized compounds should be stored in a cool, dry place away from direct light. Specific storage instructions for your product are included with your order. Consult the product details page or contact us for compound-specific guidance.",
      },
      {
        q: "How long do compounds remain stable?",
        a: "Stability depends on the specific compound and storage conditions. Proper storage in sealed, lyophilized form maintains stability for extended periods. Specific stability information is provided with your order.",
      },
      {
        q: "What is lyophilization?",
        a: "Lyophilization (freeze-drying) is a process that removes water from compounds while preserving their integrity and extending shelf life. All LuviaRx compounds are lyophilized and sealed for optimal stability and safety during transit.",
      },
    ],
  },
  {
    id: "ordering",
    label: "Ordering & Payment",
    items: [
      {
        q: "What payment methods do you accept?",
        a: "We currently accept Zelle payments only. At checkout you'll receive an order number and Zelle instructions. Send your total to luviarx@gmail.com (LuviaRx LLC) and use your order number as the memo. Your order ships once payment is verified (usually within 1 business day).",
      },
      {
        q: "Is my payment information secure?",
        a: "Yes. We do not store any banking or payment details on our servers. All transfers happen directly inside your bank's Zelle interface. We only keep your order summary and a payment status flag.",
      },
      {
        q: "Can I return or refund research compounds?",
        a: "Due to the nature of research compounds, all sales are final. Please review product descriptions and specifications carefully before ordering.",
      },
    ],
  },
  {
    id: "compliance",
    label: "Compliance & Legal",
    items: [
      {
        q: "Is it legal to purchase these compounds?",
        a: "LuviaRx operates in compliance with applicable U.S. federal and state laws. However, regulations vary by jurisdiction. You are responsible for verifying that purchasing and possessing research compounds is legal in your location.",
      },
      {
        q: "What is your privacy policy?",
        a: (
          <>
            See our full{" "}
            <Link to="/legal/privacy" className="text-sage-600 hover:text-sage-700 underline underline-offset-4">
              Privacy Policy
            </Link>
            . We protect your personal information and do not share it with third parties except as required by law.
          </>
        ),
      },
    ],
  },
  {
    id: "contact",
    label: "Contact & Support",
    items: [
      {
        q: "How do I contact customer support?",
        a: (
          <>
            Email us at{" "}
            <a href="mailto:support@luviarx.com" className="text-sage-600 hover:text-sage-700 underline underline-offset-4">
              support@luviarx.com
            </a>
            . We typically respond to inquiries within 24–48 business hours.
          </>
        ),
      },
      {
        q: "Do you offer bulk ordering?",
        a: "Please contact us directly for inquiries about larger quantities or bulk orders.",
      },
    ],
  },
];

export default function Faq() {
  return (
    <main data-testid="faq-page" className="pb-20 md:pb-28">
      <SEO
        title="Frequently Asked Questions"
        description="Answers to common LuviaRx questions on Research Use Only positioning, shipping, product quality, storage, ordering, and compliance."
        path="/faq"
      />
      {/* Header */}
      <section className="py-16 md:py-24">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-3">FAQ</p>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] tracking-tight text-charcoal-600 mb-5">
              Common questions, <span className="italic font-medium text-sage-600">answered clearly.</span>
            </h1>
            <p className="text-charcoal-400 leading-relaxed mb-7">
              Everything you need to know about LuviaRx research compounds: compliance, shipping, quality, and ordering. For anything specific to your research, we're a quick email away.
            </p>
            <a
              href="mailto:support@luviarx.com"
              className="inline-flex items-center gap-2 text-sm font-medium text-sage-600 hover:text-sage-700"
              data-testid="faq-contact-link"
            >
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              support@luviarx.com
            </a>
          </div>

          {/* Section jump links */}
          <div className="lg:col-span-7">
            <div className="surface-card p-6 md:p-7">
              <p className="overline mb-4">Jump to section</p>
              <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`faq-jump-${s.id}`}
                      className="group flex items-center justify-between gap-3 text-sm font-medium text-charcoal-500 hover:text-charcoal-600"
                    >
                      <span>{s.label}</span>
                      <span className="text-charcoal-300 group-hover:text-sage-500 transition-colors">
                        {s.items.length}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <div className="container-x space-y-16 md:space-y-20">
        {SECTIONS.map((section) => (
          <section
            key={section.id}
            id={section.id}
            data-testid={`faq-section-${section.id}`}
            className="scroll-mt-32"
          >
            <div className="mb-6 md:mb-8 flex items-baseline justify-between gap-4 flex-wrap">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-charcoal-600">
                {section.label}
              </h2>
              <span className="text-xs uppercase tracking-[0.18em] text-charcoal-300 font-medium">
                {section.items.length} {section.items.length === 1 ? "question" : "questions"}
              </span>
            </div>
            <div className="space-y-3">
              {section.items.map((item, i) => (
                <FaqRow key={`${section.id}-${i}`} item={item} sectionId={section.id} index={i} />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Refund policy hint */}
      <div className="container-x mt-12">
        <p data-testid="faq-refund-policy-note" className="text-sm text-charcoal-400 text-center">
          For information on refunds and replacements, please review our{" "}
          <a href="/refund-policy" className="text-[#8B2252] font-medium hover:underline">
            Refund Policy
          </a>.
        </p>
      </div>

      {/* Still have questions — text/email contact CTA */}
      <div className="container-x mt-12">
        <div
          data-testid="faq-still-have-questions"
          className="surface-card p-6 md:p-8 text-center"
        >
          <p className="overline mb-2">Still have questions?</p>
          <p className="text-sm text-charcoal-400 mb-4">
            Reach us anytime by text or email.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="sms:+15612880915"
              data-testid="faq-sms"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-[#8B2252] text-cream-100 hover:bg-[#7a1e48] transition-colors"
            >
              💬 Text us: (561) 288-0915
            </a>
            <a
              href="mailto:support@luviarx.com"
              data-testid="faq-email"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-white text-charcoal-600 border border-charcoal-100 hover:bg-cream-200 transition-colors"
            >
              ✉️ support@luviarx.com
            </a>
          </div>
        </div>
      </div>

      {/* Compliance footer note */}
      <div className="container-x mt-12">
        <div className="surface-card p-6 md:p-8 bg-cream-200/60">
          <p className="text-xs text-charcoal-400 leading-relaxed">
            <span className="font-medium text-charcoal-600">For Research Use Only.</span> Not for Human Consumption. Not intended to diagnose, treat, cure, or prevent any disease. Products are intended exclusively for laboratory and scientific research purposes. Not FDA approved for diagnostic or therapeutic use.
          </p>
        </div>
      </div>
    </main>
  );
}

const FaqRow = ({ item, sectionId, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`faq-row-${sectionId}-${index}`}
      className={`surface-card transition-all duration-300 ${open ? "shadow-hover" : ""}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 text-left p-5 md:p-6"
        aria-expanded={open}
      >
        <span className="font-display font-bold text-charcoal-600 text-base md:text-lg">{item.q}</span>
        <ChevronDown
          className={`w-5 h-5 text-charcoal-400 transition-transform duration-300 shrink-0 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.8}
        />
      </button>
      {open && (
        <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 text-charcoal-400 leading-relaxed text-sm md:text-base animate-fade-in">
          {item.a}
        </div>
      )}
    </div>
  );
};
